import { switchMap, takeUntil, combineLatest, Observable, of, Subscription } from 'rxjs';

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy,
  OnInit, ViewChild
} from '@angular/core';

import { SdPushPanelComponent } from '@seb/web-design-system/push-panel';
import {
  DeviceService, IExtendedLink, ILinkMedia, IPushCardWithLayout, PositionEnum,
  ScrollSpyService, SdSpyTargetDirective
} from '@seb/web-design-system/core';

import { CustomMedia, PushCardPanelComponentInterface, pushCardWithLayoutSkeleton, PushPanelConfigEnum } from '../../../models';
import { ChatService, DatalayerService, ImageService, TrackingService } from '../../../services';
import { AbstractModuleComponent } from '../abstract-module.component';
import { WrapperComponent } from '../../../components/static/core';
import { PushCardMapper } from '../../../mappers';

@Component({
  selector: 'wc-push-card-panel',
  templateUrl: './push-card-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [WrapperComponent, SdPushPanelComponent, SdSpyTargetDirective]
})
export class PushCardPanelComponent extends AbstractModuleComponent<PushCardPanelComponentInterface> implements OnInit, OnDestroy {
  @ViewChild('pushPanel', { read: ElementRef })
  pushPanel: ElementRef;

  layout: PositionEnum.left | PositionEnum.right;
  pushFullHeight: IPushCardWithLayout;
  pushTop: IPushCardWithLayout;
  pushBottom: IPushCardWithLayout;
  isPushTopPromo: boolean;
  isPushBottomPromo: boolean;
  isPushPanelHasBeenViewed = false;
  isMobile$ = false;

  private mobileSubscription: Subscription;

  constructor(
    private pushCardMapper: PushCardMapper,
    private scrollSpyService: ScrollSpyService,
    private datalayerService: DatalayerService,
    private trackingService: TrackingService,
    private chatService: ChatService,
    private imageService: ImageService,
    private deviceService: DeviceService,
    private cdRef: ChangeDetectorRef
  ) {
    super();
  }

  private static mapPushLayout(pushPosition: string): PositionEnum.left | PositionEnum.right {
    switch (pushPosition) {
      case PushPanelConfigEnum.LEFT:
        return PositionEnum.left;
      case PushPanelConfigEnum.RIGHT:
        return PositionEnum.right;
      default:
        return PositionEnum.left;
    }
  }

  override onModuleInit(module: PushCardPanelComponentInterface) {
    this.layout = PushCardPanelComponent.mapPushLayout(module.layout);
    if (this.useLazyLoad) {
      this.pushFullHeight = pushCardWithLayoutSkeleton;
      this.pushTop = pushCardWithLayoutSkeleton;
      this.pushBottom = pushCardWithLayoutSkeleton;
    }
    this.cdRef.markForCheck();
  }

  ngOnInit(): void {
    this.mobileSubscription = this.deviceService.isMobile.pipe(takeUntil(this.destroy$)).subscribe((isMobile) => {
      this.isMobile$ = isMobile;
      this.cdRef.markForCheck();
    });

    this.module$
      .pipe(
        switchMap((module: PushCardPanelComponentInterface) =>
          this.componentService.getSubComponentsLazyComponentData(module, 'componentsObjects', 'links')
        ),
        switchMap((module: PushCardPanelComponentInterface) => {
          const images: Observable<CustomMedia>[] = module.componentsObjects?.map((cmp) => {
            if (this.brandCorner) {
              cmp['brandCorner'] = this.brandCorner;
            }
            if (cmp.mediaContainer) {
              return this.imageService.getMediaFromContainer(cmp.mediaContainer);
            } else {
              return of(cmp.media);
            }
          });

          return combineLatest([of(module), combineLatest(images)]);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(([module, images]) => {
        this.pushFullHeight = this.pushCardMapper.toPushCardWithLayout(
          module.componentsObjects[0],
          this.imageService.normalizeMedia(images[0]),
          this.isMobile$
        );
        this.pushTop = this.pushCardMapper.toPushCardWithLayout(
          module.componentsObjects[1],
          this.imageService.normalizeMedia(images[1]),
          this.isMobile$
        );
        this.pushBottom = this.pushCardMapper.toPushCardWithLayout(
          module.componentsObjects[2],
          this.imageService.normalizeMedia(images[2]),
          this.isMobile$
        );

        this.isPushTopPromo = module.componentsObjects[1]?.isPushPromo
          ? JSON.parse(module.componentsObjects[1].isPushPromo)
          : false;
        this.isPushBottomPromo = module.componentsObjects[2]?.isPushPromo
          ? JSON.parse(module.componentsObjects[2].isPushPromo)
          : false;

        this.setLoaded();
        this.cdRef.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.mobileSubscription?.unsubscribe();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (!this.isPushPanelHasBeenViewed && this.scrollSpyService.isElementActive(this.pushPanel)) {
      this.isPushPanelHasBeenViewed = true;
      if (this.isPushTopPromo) {
        const promotionNameTop = this.pushTop.push.richParagraph.heading.data.title;
        this.trackingService.sendEvent('promotionView', {
          promotionNameTop
        });
        this.datalayerService.setDataLayerInternalPromotion(promotionNameTop);
      }
      if (this.isPushBottomPromo) {
        const promotionNameBottom = this.pushBottom.push.richParagraph.heading.data.title;
        this.trackingService.sendEvent('promotionView', {
          promotionNameBottom
        });
        this.datalayerService.setDataLayerInternalPromotion(promotionNameBottom);
      }
    }
  }

  onClick(event: { link: IExtendedLink | ILinkMedia; title: string }): void {
    if (event.link?.url === '<<chat>>') {
      this.chatService.clickOpenChat();
    }
    if (this.pushTop.push.richParagraph.heading.data.subtitle === event.title && this.isPushTopPromo) {
      const promotionNameTop = this.pushTop.push.richParagraph.heading.data.title;
      this.trackingService.sendEvent('promotionView', {
        promotionNameTop
      });
      this.datalayerService.setDataLayerInternalPromotion(promotionNameTop);
    }
    if (this.pushBottom.push.richParagraph.heading.data.subtitle === event.title && this.isPushBottomPromo) {
      const promotionNameBottom = this.pushBottom.push.richParagraph.heading.data.title;
      this.trackingService.sendEvent('promotionView', {
        promotionNameBottom
      });
      this.datalayerService.setDataLayerInternalPromotion(promotionNameBottom);
    }
  }

  onCardPushClick(title: string): void {
    this.trackingService.sendEvent('clickCardFrames', {
      componentName: title
    });
  }
}
